'use client';

import { useEffect, useState } from 'react';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { useMcpSettings } from '@/hooks/queries/useMcpSettings';
import { ApiError } from '@/lib/api';
import type { McpConnectedAppDto } from '@/lib/api';
import { track } from '@/lib/telemetry';

const LAST_USED = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const CLAUDE_STEPS = [
  'Abra Personalizar → Conectores (Customize → Connectors).',
  'Clique em + e escolha Adicionar conector personalizado (Add custom connector).',
  'Dê o nome Unare, cole o endereço acima e clique em Adicionar.',
  'Clique em Conectar e entre com a sua conta da Vértice.',
];

const CHATGPT_STEPS = [
  'Abra Configurações → Apps e conectores (Settings → Apps & Connectors) e, em Avançado, ative o Modo de desenvolvedor (Developer mode).',
  'Volte a Apps e conectores e clique em Criar (Create).',
  'Dê o nome Unare, cole o endereço acima, escolha OAuth e confirme.',
  'Entre com a sua conta da Vértice.',
];

/**
 * "Unare no Claude e no ChatGPT" on Perfil (VER-492): the address to paste into Claude or ChatGPT,
 * how to add it, and the apps that already used it. Every plan includes it (VER-503: a tool call has no
 * AI cost, so the API has no plan gate or call limit on it). The address comes from the API; an
 * environment with the connector off, an API without the endpoint (404), or no address renders
 * nothing. There is no revoke button: the identity provider lists no person's OAuth grants, so
 * disconnecting happens in Claude or ChatGPT.
 */
export function McpConnectorSection() {
  const { data, error, mutate } = useMcpSettings();
  // Without an address (null, empty or omitted) there is nothing to set up: no section rather than
  // a header over a missing setup. The same truthy test as the setup's own render below.
  const visible = data?.enabled === true && Boolean(data.serverUrl);
  const failed = !data && error !== undefined && !(error instanceof ApiError && error.status === 404);

  useEffect(() => {
    if (visible) track('mcp.settings_viewed');
  }, [visible]);

  if (failed) return <LoadFailed onRetry={() => void mutate()} />;
  if (!data || !visible) return null;

  return (
    <section data-testid="mcp-connector" className="lg:pl-8">
      <SectionHeader />
      {data.serverUrl && <ConnectorSetup serverUrl={data.serverUrl} apps={data.apps} />}
    </section>
  );
}

function SectionHeader() {
  return (
    <div className="mb-4">
      <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
        Unare no Claude e no ChatGPT
      </p>
      <p className="mt-1 text-sm text-text-secondary">
        Pergunte sobre suas finanças direto no Claude ou no ChatGPT, com os mesmos números do app.
      </p>
    </div>
  );
}

function LoadFailed({ onRetry }: { onRetry: () => void }) {
  useEffect(() => {
    track('friction.load_failed', { surface: 'mcp_settings' });
  }, []);

  const retry = () => {
    track('friction.retry_clicked', { surface: 'mcp_settings' });
    onRetry();
  };

  return (
    <section data-testid="mcp-connector" className="lg:pl-8">
      <SectionHeader />
      <div className="flex items-center justify-between gap-4 rounded-lg border border-error-border p-4 text-sm">
        <p className="text-text-secondary">Não foi possível carregar esta seção.</p>
        <Button type="button" variant="outline" size="sm" onClick={retry}>
          Tentar novamente
        </Button>
      </div>
    </section>
  );
}

function ConnectorSetup({ serverUrl, apps }: { serverUrl: string; apps: McpConnectedAppDto[] }) {
  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-sm font-semibold text-text-primary">Endereço do conector</p>
        <CopyableAddress value={serverUrl} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Steps title="No Claude" steps={CLAUDE_STEPS} />
        <Steps
          title="No ChatGPT"
          steps={CHATGPT_STEPS}
          note="Conectores próprios no ChatGPT exigem um plano pago (Plus ou superior)."
        />
      </div>
      <p className="text-sm text-text-secondary">
        Na primeira vez, a Vértice pede a sua permissão: “Fazer perguntas à Unare sobre suas finanças”.
      </p>
      <ConnectedApps apps={apps} />
      <HowItWorks />
    </div>
  );
}

function CopyableAddress({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        track('action.mcp.copy_url');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-sunken px-3 py-2">
      <code className="min-w-0 flex-1 truncate font-mono text-xs text-text-primary">{value}</code>
      <Button type="button" variant="outline" size="sm" onClick={onCopy}>
        {copied ? <CheckIcon className="size-3.5" weight="bold" /> : <CopyIcon className="size-3.5" weight="bold" />}
        {copied ? 'Copiado' : 'Copiar'}
      </Button>
    </div>
  );
}

function Steps({ title, steps, note }: { title: string; steps: string[]; note?: string }) {
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-text-primary">{title}</p>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-text-secondary">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {note && <p className="mt-2 text-xs text-text-tertiary">{note}</p>}
    </div>
  );
}

function ConnectedApps({ apps }: { apps: McpConnectedAppDto[] }) {
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-text-primary">Aplicativos conectados</p>
      {apps.length === 0 ? (
        <p className="text-sm text-text-secondary">Nenhum aplicativo usou a Unare ainda.</p>
      ) : (
        <ul className="divide-y divide-border-subtle">
          {apps.map((app) => (
            <li key={`${app.name}:${app.firstUsedAt}`} className="flex items-center justify-between gap-4 py-2 text-sm">
              <span className="text-text-primary">{app.name}</span>
              <span className="text-text-secondary">Último uso em {LAST_USED.format(new Date(app.lastUsedAt))}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-text-tertiary">
        Para desconectar, remova a Unare no Claude (Personalizar → Conectores) ou no ChatGPT (Configurações → Apps e
        conectores).
      </p>
    </div>
  );
}

function HowItWorks() {
  return (
    <details className="text-sm text-text-secondary">
      <summary className="cursor-pointer font-semibold text-text-primary">Como funciona</summary>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        <li>
          A Unare responde sobre contas, cartões e faturas, transações, orçamento, metas, investimentos e Espaços, com
          os mesmos números do app.
        </li>
        <li>Ela só consulta: nunca altera nada na sua conta e não exporta seus dados brutos.</li>
        <li>
          O Claude ou o ChatGPT recebe as respostas da Unare e o resultado das consultas. As instruções internas e as
          consultas ao banco de dados nunca saem da Vértice.
        </li>
      </ul>
    </details>
  );
}
